import { supabase } from "./client";
import { commissionBaseBeforeLocalDiscount } from "./edge-functions/_shared/commissionPricing";
import { normalizeCommissionRate } from "../local/paymentSettings";
import {
  buildPaymentRailSnapshot,
  isDigitalPaymentRail,
  normalizePaymentRail,
} from "../payments/paymentRails";

const COMMISSION_SETTLEMENT_FUNCTION_NAME = "settle-digital-commission";

const SETTLED_STATUSES = new Set(["paid", "closed"]);
const LEDGER_ENTRY_FACTORS = Object.freeze({
  debt_created: 1,
  debt_adjusted: 1,
  debt_applied: -1,
  debt_cleared: -1,
});

const toSafeText = (value, maxLength = 240) =>
  String(value || "")
    .trim()
    .slice(0, maxLength);

const toMoney = (value) => {
  const parsed = Number(value || 0);
  return Number.isFinite(parsed) ? Math.round(parsed * 100) / 100 : 0;
};

const normalizeMetadata = (value) =>
  value && typeof value === "object" && !Array.isArray(value) ? value : {};

const normalizeFunctionError = (error, data, fallbackMessage) => {
  const transportMessage = toSafeText(error?.message, 240);
  const backendMessage = toSafeText(
    data?.error || data?.message || data?.details || data?.hint,
    240
  );

  if (backendMessage && transportMessage.toLowerCase().includes("non-2xx")) {
    return backendMessage;
  }

  return backendMessage || transportMessage || fallbackMessage;
};

const extractFunctionErrorPayload = async (error, data) => {
  if (data && typeof data === "object") {
    return data;
  }

  const response = error?.context;

  if (!response) {
    return null;
  }

  const readableResponse =
    typeof response.clone === "function" ? response.clone() : response;

  try {
    const contentType = toSafeText(
      readableResponse.headers?.get?.("content-type"),
      120
    ).toLowerCase();

    if (contentType.includes("application/json")) {
      return await readableResponse.json();
    }

    const rawText = toSafeText(await readableResponse.text(), 500);

    if (!rawText) {
      return null;
    }

    try {
      return JSON.parse(rawText);
    } catch {
      return { error: rawText };
    }
  } catch {
    return null;
  }
};

const isSettledOperation = (operation) =>
  SETTLED_STATUSES.has(toSafeText(operation?.status, 40).toLowerCase());

const resolveOperationMethod = (operation) =>
  toSafeText(operation?.selectedMethod || operation?.requestedMethod, 40).toLowerCase();

const resolveOperationRail = (operation) => {
  const derivedRail = buildPaymentRailSnapshot({
    source: operation?.source,
    checkoutMethod: resolveOperationMethod(operation),
    paymentProvider: operation?.paymentRailProvider || operation?.provider || "",
    splitMode: operation?.paymentSplitMode,
    isKioskExperience: operation?.source === "kiosk",
  }).rail;

  return normalizePaymentRail(operation?.paymentRail || derivedRail);
};

const isLocalSettledOperation = (operation) =>
  isSettledOperation(operation) && !isDigitalPaymentRail(resolveOperationRail(operation));

const calculateLedgerOutstandingAmount = (entries = []) =>
  toMoney(
    entries.reduce((sum, entry) => {
      const factor = LEDGER_ENTRY_FACTORS[toSafeText(entry?.entry_type, 40)] || 0;
      return sum + toMoney(entry?.amount) * factor;
    }, 0)
  );

const normalizePreparationNumber = (value) => {
  const parsed = Number(value || 0);
  return Number.isFinite(parsed) && parsed > 0 ? Math.round(parsed) : null;
};

export const getBranchCommissionDebtSummary = async (branch) => {
  const branchId = toSafeText(branch?.id || branch, 120);

  if (!supabase || !branchId) {
    return {
      ok: false,
      outstandingDebtAmount: 0,
      totalCreatedAmount: 0,
      totalAppliedAmount: 0,
      entriesCount: 0,
    };
  }

  const { data: entries, error } = await supabase
    .from("branch_commission_ledger")
    .select("entry_type, amount")
    .eq("branch_id", branchId);

  if (error) {
    throw new Error(
      toSafeText(
        error.message ||
          "No pudimos leer el resumen de deuda acumulada de esta sucursal.",
        240
      )
    );
  }

  const normalizedEntries = Array.isArray(entries) ? entries : [];
  const totalCreatedAmount = toMoney(
    normalizedEntries.reduce((sum, entry) => {
      const entryType = toSafeText(entry?.entry_type, 40);
      if (entryType !== "debt_created" && entryType !== "debt_adjusted") {
        return sum;
      }

      return sum + toMoney(entry?.amount);
    }, 0)
  );
  const totalAppliedAmount = toMoney(
    normalizedEntries.reduce((sum, entry) => {
      const entryType = toSafeText(entry?.entry_type, 40);
      if (entryType !== "debt_applied" && entryType !== "debt_cleared") {
        return sum;
      }

      return sum + toMoney(entry?.amount);
    }, 0)
  );

  return {
    ok: true,
    branchId,
    entriesCount: normalizedEntries.length,
    outstandingDebtAmount: calculateLedgerOutstandingAmount(normalizedEntries),
    totalCreatedAmount,
    totalAppliedAmount,
  };
};

export const syncManualCommissionDebtForOperation = async ({
  branch,
  operation,
  note = "",
  metadata = {},
} = {}) => {
  if (!supabase) {
    return {
      ok: false,
      skipped: true,
      reason: "supabase_not_configured",
    };
  }

  const branchId = toSafeText(branch?.id || branch, 120);
  const paymentOperationId = toSafeText(operation?.id, 160);
  const orderId = toSafeText(operation?.orderId, 160);

  if (!branchId || !paymentOperationId) {
    return {
      ok: false,
      skipped: true,
      reason: "missing_identifiers",
    };
  }

  const grossAmount = toMoney(operation?.grossAmount ?? operation?.amount);
  const commissionRate = normalizeCommissionRate(operation?.commissionRate);
  const selectedMethod = resolveOperationMethod(operation);
  const paymentRail = resolveOperationRail(operation);

  if (!isLocalSettledOperation(operation)) {
    return {
      ok: true,
      applied: false,
      skipped: true,
      reason: "not_local_settled_operation",
      targetDebtAmount: 0,
      outstandingDebtAmount: null,
    };
  }

  const commissionBaseAmount = commissionBaseBeforeLocalDiscount(operation?.pricing, grossAmount);
  const targetDebtAmount = toMoney(commissionBaseAmount * commissionRate);

  const { data, error } = await supabase.rpc("sync_branch_manual_commission", {
    p_branch_id: branchId,
    p_operation_id: paymentOperationId,
    p_order_id: orderId || null,
    p_requested: targetDebtAmount,
    p_source_method: selectedMethod || null,
    p_metadata: {
      ...normalizeMetadata(metadata),
      note: toSafeText(note, 240),
      source: toSafeText(operation?.source, 40),
      canal: toSafeText(operation?.canal, 60),
      status: toSafeText(operation?.status, 40),
      paymentRail,
      customerName: toSafeText(operation?.customerName, 80),
      preparationNumber: normalizePreparationNumber(operation?.preparationNumber),
      itemsSummary: toSafeText(operation?.itemsSummary, 900),
      mesa: toSafeText(operation?.mesa, 120),
      serviceType: toSafeText(operation?.serviceType, 80),
      grossAmount,
      commissionBaseAmount,
      commissionRate,
      targetDebtAmount,
    },
  });

  if (error || !data?.ok) {
    throw new Error(
      toSafeText(
        error?.message ||
          "No pudimos guardar la deuda de comision acumulada de esta operacion.",
        240
      )
    );
  }

  return data;
};

export const getBranchCommissionCap = async (branchId) => {
  if (!supabase || !branchId) return null;
  const { data, error } = await supabase.rpc("get_branch_commission_cap", {
    p_branch_id: branchId,
  });
  if (error) throw new Error(error.message);
  return data;
};

export const finalizeApprovedDigitalCommission = async ({
  branchId,
  orderId,
  paymentOperationId,
  paymentIntentId,
  sourceChannel = "unknown",
  sourceMode = "unknown",
  metadata = {},
} = {}) => {
  if (!supabase) {
    throw new Error(
      "Supabase no esta configurado. No podemos cerrar la comision del cobro digital."
    );
  }

  const body = {
    branchId: toSafeText(branchId, 120),
    orderId: toSafeText(orderId, 160),
    paymentOperationId: toSafeText(paymentOperationId, 160),
    paymentIntentId: toSafeText(paymentIntentId, 120),
    sourceChannel: toSafeText(sourceChannel, 80) || "unknown",
    sourceMode: toSafeText(sourceMode, 80) || "unknown",
    metadata: normalizeMetadata(metadata),
  };

  if (!body.branchId || !body.paymentOperationId || !body.paymentIntentId) {
    return {
      ok: false,
      skipped: true,
      reason: "missing_identifiers",
    };
  }

  const { data, error } = await supabase.functions.invoke(
    COMMISSION_SETTLEMENT_FUNCTION_NAME,
    { body }
  );

  if (error) {
    const errorPayload = await extractFunctionErrorPayload(error, data);

    throw new Error(
      normalizeFunctionError(
        error,
        errorPayload,
        "No pudimos cerrar la comision del cobro digital."
      )
    );
  }

  return data && typeof data === "object" ? data : { ok: true };
};
